'use client';

import * as React from 'react';

export interface UseIsMobileOptions {
  /** Value returned during SSR AND on the first client render before
   *  the post-mount effect swaps to the live matchMedia value. Default:
   *  `false` (desktop-first). Set to `true` if your app shell is
   *  mobile-first and the server would rather ship that markup.
   *
   *  Audit #2 (2026-05-16): the prior implementation read matchMedia
   *  inside the useState initializer when `typeof window !== 'undefined'`.
   *  On hydration that created an immediate mismatch — the server
   *  rendered with `ssrFallback`, the client's first render saw the
   *  live viewport, and React's hydration assert flagged the divergence
   *  on every page load. The fix below always returns `ssrFallback` on
   *  the first render and swaps to the live value inside `useEffect`,
   *  costing at most one post-hydration render flip but eliminating
   *  hydration mismatches across Next.js, Remix, and Astro. */
  ssrFallback?: boolean;
}

/** @deprecated Use `useBreakpoint()` instead. Will be removed in v6.
 *  Migration: `const isMobile = useBreakpoint() === null || useBreakpoint() === 'sm';`
 *
 *  Viewport media-query hook with deterministic SSR + hydration behaviour.
 *  - First render (server AND client): returns `ssrFallback`.
 *  - Post-mount: `useEffect` reads the live matchMedia value and updates
 *    state. React renders once more if the live value differs.
 *  - Subsequent viewport changes fire through the matchMedia `change`
 *    listener.
 *
 *  Trade-off vs v4: mobile devices see one extra render swap (from
 *  ssrFallback to mobile) on first page load. The compensating win is
 *  zero hydration mismatches across SSR-capable frameworks. */
export function useIsMobile(
  query = '(max-width: 820px)',
  options: UseIsMobileOptions = {},
): boolean {
  const { ssrFallback = false } = options;

  /* Always start with the SSR fallback so the server-rendered HTML
     matches the first client render. The live value comes through the
     effect below — at most one re-render after hydration. */
  const [match, setMatch] = React.useState<boolean>(ssrFallback);

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatch(e.matches);
    /* Reconcile on mount — swap to the live value if it differs from
       ssrFallback. Functional setState skips the extra render when the
       live value already equals fallback. */
    setMatch((prev) => (prev === mq.matches ? prev : mq.matches));
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, [query]);

  return match;
}
